/**
 * Data export utilities for downloading data as JSON, CSV, or other formats
 */

/**
 * Download a file to the user's device
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Export data as JSON file
 */
export function exportAsJson(data: object, filename: string): void {
  const json = JSON.stringify(data, null, 2)
  downloadFile(json, `${filename}.json`, 'application/json')
}

/**
 * Export array of objects as CSV file
 */
export function exportAsCsv<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  options?: {
    headers?: string[]
    columns?: (keyof T)[]
  }
): void {
  if (data.length === 0) {
    downloadFile('', `${filename}.csv`, 'text/csv')
    return
  }

  // Determine columns to export
  const columns = options?.columns || (Object.keys(data[0]) as (keyof T)[])

  // Create header row
  const headers = options?.headers || columns.map(String)

  // Create CSV rows
  const rows = data.map(item =>
    columns.map(col => {
      const value = item[col]
      // Handle different value types
      if (value === null || value === undefined) {
        return ''
      }
      if (Array.isArray(value)) {
        return `"${value.join('; ')}"`
      }
      if (typeof value === 'string') {
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        if (value.includes(',') || value.includes('\n') || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`
        }
        return value
      }
      return String(value)
    }).join(',')
  )

  const csv = [headers.join(','), ...rows].join('\n')
  downloadFile(csv, `${filename}.csv`, 'text/csv;charset=utf-8')
}

/**
 * Export protein sequence as FASTA format
 */
export function exportAsFasta(
  sequence: string,
  header: string,
  filename: string
): void {
  // Wrap sequence to 80 characters per line (FASTA standard)
  const wrappedSequence = sequence.match(/.{1,80}/g)?.join('\n') || sequence
  const fasta = `>${header}\n${wrappedSequence}`
  downloadFile(fasta, `${filename}.fasta`, 'text/plain')
}

/**
 * Export cluster data with genes
 */
export interface ClusterExportData {
  mibig_accession: string
  organism_name: string
  biosyn_class?: string[]
  ncbi_tax_id?: string | number
  gene_count?: number
  compounds?: { name: string; database_id?: string[] }[]
}

export interface GeneExportData {
  protein_id: string
  product?: string
  uniprot_ids?: string[]
}

export function exportClusterData(
  cluster: ClusterExportData,
  genes: GeneExportData[] = []
): void {
  const filename = `cluster_${cluster.mibig_accession}`

  // Export as JSON with cluster info and genes
  const exportData = {
    ...cluster,
    genes: genes.map(gene => ({
      protein_id: gene.protein_id,
      product: gene.product,
      uniprot_ids: gene.uniprot_ids,
    })),
  }
  exportAsJson(exportData, filename)
}

/**
 * Export cluster browser results
 */
export interface ClusterListItem {
  mibig_accession: string
  organism_name: string
  biosyn_class?: string[]
  gene_count?: number
  compounds?: { name: string }[]
}

export function exportClusterList(clusters: ClusterListItem[], filename: string = 'clusters'): void {
  const data = clusters.map(cluster => ({
    mibig_accession: cluster.mibig_accession,
    organism: cluster.organism_name,
    biosyn_class: cluster.biosyn_class?.join('; ') || '',
    gene_count: cluster.gene_count ?? 0,
    compounds: cluster.compounds?.map(c => c.name).join('; ') || '',
  }))

  exportAsCsv(data, filename, {
    headers: ['MIBiG Accession', 'Organism', 'Biosynthetic Class', 'Gene Count', 'Compounds'],
    columns: ['mibig_accession', 'organism', 'biosyn_class', 'gene_count', 'compounds'],
  })
}

/**
 * Export BLAST results
 */
export interface BlastHitExport {
  hit_id: string
  description?: string
  evalue: number
  identity: number
  coverage: number
  score?: number
}

export function exportBlastResults(
  hits: BlastHitExport[],
  database: string,
  filename: string = 'blast_results'
): void {
  const data = hits.map(hit => ({
    hit_id: hit.hit_id,
    description: hit.description || '',
    evalue: hit.evalue,
    identity: hit.identity,
    coverage: hit.coverage,
    score: hit.score || '',
  }))

  exportAsCsv(data, `${filename}_${database}`, {
    headers: ['Hit ID', 'Description', 'E-value', 'Identity (%)', 'Coverage (%)', 'Score'],
    columns: ['hit_id', 'description', 'evalue', 'identity', 'coverage', 'score'],
  })
}
